function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useState, useRef, useLayoutEffect } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { SpinnerReact } from 'common/components/spinner';
import { SpinnerSize } from 'common/components/spinner/types';
import { Rollout, Variant } from 'app_api/projects/feature_flags/types';
import { ButtonReact } from 'common/components/button';
import { ButtonTheme } from 'common/components/button/constants';
import { InputReact } from 'common/components/input';
import { SvgIconReact } from 'common/components/svg-icon';
import { useTranslation } from 'common/i18n/hooks';
import { CohortsBuilderReact } from 'common/query-builder/cohorts-query-builder';
import { SerializedCohort } from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import { CohortsQueryBuilderUpdate } from 'common/query-builder/cohorts-query-builder/types';
import { Cohort } from 'common/query-builder/cohorts-query-builder/models/cohort';
import { fetchPeopleCountByCohort } from 'common/report/queries/query-api/people-stats-client';
import { useFeatureFlagPageContext } from 'iron/apps/feature-flags/src/components/context';
import styles from './index.module.styl';
interface FeatureFlagRolloutsProps {
  canUpdate?: boolean;
  variants?: Variant[];
  rollouts: Rollout[];
  setRollouts: (rollouts: Rollout[]) => void;
}
export function FeatureFlagRollouts(_ref: FeatureFlagRolloutsProps) {
  let {
    canUpdate = false,
    rollouts,
    variants,
    setRollouts
  }: FeatureFlagRolloutsProps = _ref;
  const i18n = useTranslation();
  const [editingRolloutNameIndex, setEditingRolloutNameIndex] = useState<number | null>(null);
  const handleCohortUpdate = (cohort: SerializedCohort, index: number) => {
    if (cohort) {
      const updatedRollouts = rollouts.map((rollout, i) => {
        if (i === index) {
          var _cohort$groups, _cohort$groups2, _cohort$groups3, _cohort$groups4;
          if ((_cohort$groups = cohort.groups) !== null && _cohort$groups !== void 0 && _cohort$groups.length && ((_cohort$groups2 = cohort.groups) === null || _cohort$groups2 === void 0 ? void 0 : _cohort$groups2.length) > 1 || (_cohort$groups3 = cohort.groups) !== null && _cohort$groups3 !== void 0 && (_cohort$groups3 = _cohort$groups3[0].filters) !== null && _cohort$groups3 !== void 0 && _cohort$groups3.length && cohort.groups[0].filters.length > 0 || ((_cohort$groups4 = cohort.groups) === null || _cohort$groups4 === void 0 || (_cohort$groups4 = _cohort$groups4[0].event) === null || _cohort$groups4 === void 0 ? void 0 : _cohort$groups4.value) !== "$all_users") {
            return _objectSpread(_objectSpread({}, rollout), {}, {
              cohort_definition: _objectSpread({}, cohort)
            });
          }
          rollout = cloneDeep(rollout);
          delete rollout.cohort_definition;
        }
        return rollout;
      });
      setRollouts(updatedRollouts);
    }
  };
  const handleRolloutPercentageChange = (value: string, index: number) => {
    // Convert percentage to decimal format (0-1)
    const percentValue = Math.min(Math.max(parseFloat(value) || 0, 0), 100) / 100;
    const updatedRollouts = rollouts.map((rollout, i) => i === index ? _objectSpread(_objectSpread({}, rollout), {}, {
      rollout_percentage: percentValue
    }) : rollout);
    setRollouts(updatedRollouts);
  };
  const handleRolloutNameChange = (name: string, index: number) => {
    const updatedRollouts = rollouts.map((rollout, i) => i === index ? _objectSpread(_objectSpread({}, rollout), {}, {
      name
    }) : rollout);
    setRollouts(updatedRollouts);
    setEditingRolloutNameIndex(null);
  };
  const handleVariantOverrideToggle = (variantKey: string, index: number) => {
    const updatedRollouts = rollouts.map((rollout, i) => {
      if (i === index) {
        var _rollout$variant_over;
        const isVariantAlreadyOverridden = ((_rollout$variant_over = rollout.variant_override) === null || _rollout$variant_over === void 0 ? void 0 : _rollout$variant_over.key) === variantKey;
        return _objectSpread(_objectSpread({}, rollout), {}, {
          variant_override: isVariantAlreadyOverridden ? null : {
            key: variantKey
          }
        });
      }
      return _objectSpread(_objectSpread({}, rollout), {}, {
        variant_override: null
      });
    });
    setRollouts(updatedRollouts);
  };
  const handleAddAssignmentGroup = () => {
    const newRollout: Rollout = {
      name: "Production Users",
      rollout_percentage: 1.0
    };
    const updatedRollouts = [...rollouts, newRollout];
    setRollouts(updatedRollouts);
  };
  return <div className={styles.rollouts} data-sentry-component="FeatureFlagRollouts" data-sentry-source-file="index.tsx">
      <h2 className={styles.rolloutsHeader}>{i18n.t("feature-flags.page.rollouts.title")}</h2>
      {rollouts.map((rollout, index) => <RolloutRow key={"".concat(rollout.name, "-").concat(index)} rollout={rollout} variants={variants !== null && variants !== void 0 ? variants : []} canUpdate={canUpdate} canDelete={index > 0} isEditing={editingRolloutNameIndex === index} handleStartEditingName={() => setEditingRolloutNameIndex(index)} handleStopEditingName={() => setEditingRolloutNameIndex(null)} handleCohortUpdate={cohort => handleCohortUpdate(cohort, index)} handleRolloutNameChange={name => handleRolloutNameChange(name, index)} handleRolloutPercentageChange={value => handleRolloutPercentageChange(value, index)} handleDeleteRollout={() => {
      const updatedRollouts = rollouts.filter((_, i) => i !== index);
      setRollouts(updatedRollouts);
    }} handleVariantOverrideToggle={variantKey => handleVariantOverrideToggle(variantKey, index)} />)}
      {canUpdate && <div className={styles.rolloutActions}>
          <ButtonReact theme={ButtonTheme.Secondary} onClick={handleAddAssignmentGroup}>
            {i18n.t("feature-flags.page.rollouts.add-assignment-group")}
          </ButtonReact>
        </div>}
    </div>;
}
interface RolloutRowProps {
  rollout: Rollout;
  variants: Variant[];
  canUpdate: boolean;
  canDelete: boolean;
  isEditing: boolean;
  handleStartEditingName: () => void;
  handleStopEditingName: () => void;
  handleCohortUpdate: (cohort: SerializedCohort) => void;
  handleRolloutNameChange: (name: string) => void;
  handleRolloutPercentageChange: (value: string) => void;
  handleDeleteRollout: () => void;
  handleVariantOverrideToggle: (variantKey: string, index: number) => void;
}
function RolloutRow(_ref2: RolloutRowProps) {
  var _rollout$variant_over2;
  let {
    rollout,
    variants,
    canUpdate,
    canDelete,
    isEditing,
    handleStartEditingName,
    handleStopEditingName,
    handleCohortUpdate,
    handleRolloutNameChange,
    handleRolloutPercentageChange,
    handleDeleteRollout,
    handleVariantOverrideToggle
  }: RolloutRowProps = _ref2;
  const i18n = useTranslation();
  const {
    canEdit,
    projectId,
    workspaceId
  } = useFeatureFlagPageContext();
  const serializedCohort = rollout.cohort_definition as SerializedCohort;
  const RolloutNameButton = (_ref3: {
    name: string;
  }) => {
    let {
      name
    }: {
      name: string;
    } = _ref3;
    return <h2 className={styles.rolloutName} onClick={() => canUpdate && handleStartEditingName()} style={{
      cursor: canUpdate ? "pointer" : "default"
    }} data-sentry-component="RolloutNameButton" data-sentry-source-file="index.tsx">
      {name}
    </h2>;
  };
  const RolloutNameInput = (_ref4: {
    name: string;
  }) => {
    let {
      name
    }: {
      name: string;
    } = _ref4;
    const inputRef = useRef(null);
    useLayoutEffect(() => {
      // @ts-ignore
      inputRef.current.focus();
    }, []);
    return <InputReact ref={inputRef} className={styles.rolloutNameInput} disabled={!canEdit} value={name} onBlur={(ev: TODO) => {
      ev.preventDefault();
      handleStopEditingName();
    }} onKeydown={(ev: KeyboardEvent) => {
      if (ev.key === "Escape") {
        ev.preventDefault();
        handleStopEditingName();
      } else if (ev.key === "Enter") {
        ev.preventDefault();
        handleRolloutNameChange((ev.target as HTMLInputElement).value);
      }
    }} data-sentry-element="InputReact" data-sentry-component="RolloutNameInput" data-sentry-source-file="index.tsx" />;
  };
  const aVariantIsOverridden = rollout.variant_override !== undefined && rollout.variant_override !== null;
  const overriddenVariantKey = (_rollout$variant_over2 = rollout.variant_override) === null || _rollout$variant_over2 === void 0 ? void 0 : _rollout$variant_over2.key;
  const hasCohorts = React.useRef<boolean>(!isEmpty(serializedCohort) || serializedCohort === undefined);
  return <div className={styles.rolloutConfigContainer} data-sentry-component="RolloutRow" data-sentry-source-file="index.tsx">
      {canUpdate && canDelete && <SvgIconReact className={styles.deleteRollout} icon='trashcan' onClick={() => handleDeleteRollout()} />}
      {isEditing ? <RolloutNameInput name={rollout.name || ""} /> : <RolloutNameButton name={rollout.name || ""} />}
      <div className={"".concat(styles.rolloutConfigRow, " ").concat(!canEdit && styles.disabled)}>
        <div className={styles.cohortBuilder}>
          <div className={clsx(styles.cohortBuilderInner, {
          [styles.empty]: !hasCohorts.current
        })}>
            <CohortsBuilderReact cohort={serializedCohort} onCohortUpdate={(_ref5: CohortsQueryBuilderUpdate) => {
            let {
              cohort
            }: CohortsQueryBuilderUpdate = _ref5;
            hasCohorts.current = true;
            handleCohortUpdate(cohort.serialize());
          }} projectId={projectId} workspaceId={workspaceId} projectHasCohorts supportB2bCohorts nonCollapsible data-sentry-element="CohortsBuilderReact" data-sentry-source-file="index.tsx" />
          </div>
          {hasCohorts.current && <>
              <div className={styles.cohortSpacer} />
              <CohortCount serializedCohort={serializedCohort} projectId={projectId} workspaceId={workspaceId} />
            </>}
        </div>
        <div className={styles.arrowIconWrapper}>
          <SvgIconReact icon='arrow-right' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
        </div>

        <div className={styles.rolloutBox}>
          <div className={styles.rolloutTitle}>{i18n.t("feature-flags.page.rollouts.rollout-title")}</div>
          <div className={styles.variantSplitVisual}>
            {variants === null || variants === void 0 ? void 0 : variants.map(variant => {
            const currentVariantIsOverridden = overriddenVariantKey === variant.key;
            const widthFactor = aVariantIsOverridden ? currentVariantIsOverridden ? 1 : 0 : variant.split;
            const widthFactorWithRolloutPercentage = widthFactor * rollout.rollout_percentage;
            return <div className={styles.variantBar} key={variant.key} {...widthFactor === 1 ? {
              'data-only-variant-bar': ""
            } : {}} style={{
              width: "".concat(widthFactorWithRolloutPercentage * 100, "%"),
              height: "16px"
            }} />;
          })}
          </div>
          <div className={styles.percentageContainer}>
            <InputReact className={styles.percentageInput} disabled={!canEdit} value={String(Math.round(rollout.rollout_percentage * 100))} onInput={(ev: TODO) => handleRolloutPercentageChange(ev.target.value)} // TODO why is pressing the enter key required to trigger rerender of the component?
          type='number' min='0' max='100' data-sentry-element="InputReact" data-sentry-source-file="index.tsx" />
            %<span className={styles.enabledLabel}>{i18n.t("feature-flags.page.rollouts.enabled")}</span>
          </div>
          <div className={styles.variantFrame}>
            <div className={styles.variantIndent} />
            <div className={styles.variantContent}>
              {variants === null || variants === void 0 ? void 0 : variants.map((variant, variantIndex) => {
              var _rollout$variant_over3;
              return <div key={variant.key} className={styles.variantRowItem} onClick={() => handleVariantOverrideToggle(variant.key, variantIndex)}>
                  <span className={"".concat(styles.variantPercentage)} style={{
                  color: aVariantIsOverridden && overriddenVariantKey === variant.key || !aVariantIsOverridden && variant.split > 0 ? "inherit" : "var(--gray2)"
                }}>
                    {aVariantIsOverridden ? overriddenVariantKey === variant.key ? "100%" : "0%" : "".concat(variant.split * 100, "%")}
                  </span>
                  <div className={styles.variantLabel}>
                    <span className={"".concat(variantIndex % 2 === 0 ? styles.variantColorIndicatorPurple : styles.variantColorIndicatorLava)} />
                    <span className={styles.variantName} style={{
                    color: aVariantIsOverridden && overriddenVariantKey === variant.key || !aVariantIsOverridden && variant.split > 0 ? "inherit" : "var(--gray2)"
                  }}>
                      {variant.key}
                    </span>
                  </div>
                  {((_rollout$variant_over3 = rollout.variant_override) === null || _rollout$variant_over3 === void 0 ? void 0 : _rollout$variant_over3.key) === variant.key ? <SvgIconReact className={styles.variantX} icon='x' /> : <SvgIconReact className={styles.variantTarget} icon='crosshairs' />}
                </div>;
            })}
            </div>
          </div>
          <div className={styles.deducedRolloutExclusionContainer}>
            <span className={styles.deducedRolloutExclusionElement}>
              {Math.round((1 - rollout.rollout_percentage) * 100)}%
            </span>
            <span className={styles.deducedRolloutExclusionElement}>
              {i18n.t("feature-flags.page.rollouts.excluded")}
            </span>
          </div>
        </div>
      </div>
    </div>;
}
interface CohortCountProps {
  serializedCohort: SerializedCohort;
  projectId: number;
  workspaceId: number;
}
function CohortCount(_ref6: CohortCountProps) {
  var _serializedCohort$gro;
  let {
    serializedCohort,
    projectId,
    workspaceId
  }: CohortCountProps = _ref6;
  const i18n = useTranslation();

  // Only query cohort count if it doesn't target the entire userbase.
  const hasFilters = serializedCohort && ((_serializedCohort$gro = serializedCohort.groups) === null || _serializedCohort$gro === void 0 ? void 0 : _serializedCohort$gro.some(group => (group === null || group === void 0 ? void 0 : group.filters) && group.filters.length > 0));
  const {
    data: cohortCount,
    isLoading,
    error
  } = useQuery({
    queryKey: ["cohortCount", serializedCohort, projectId, workspaceId],
    queryFn: async () => {
      const cohortModel = Cohort.parseCohort(serializedCohort);
      const statsResponse = await fetchPeopleCountByCohort(cohortModel, true, cohortModel.dataGroupId, {
        projectId,
        workspaceId
      });
      return statsResponse.results;
    },
    enabled: hasFilters
  });
  if (!hasFilters) {
    return null;
  }
  if (isLoading) {
    return <div className={styles.cohortUserCount}>
        <SpinnerReact size={SpinnerSize.Small} />
        {i18n.t("feature-flags.page.rollouts.loading-cohort-count")}
      </div>;
  }
  if (error) {
    return <div className={styles.cohortUserCount}>{i18n.t("feature-flags.page.rollouts.cohort-count-error")}</div>;
  }
  if (cohortCount !== undefined && Number.isInteger(cohortCount)) {
    return <div className={styles.cohortUserCount}>
        {i18n.t("feature-flags.page.rollouts.cohort-user-count", {
        cohortCount: cohortCount.toLocaleString()
      })}
      </div>;
  }
  return null;
}